import { useEffect, useState } from "react";
import { Autocomplete } from "@/components/Autocomplete";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextareaInput } from "@/components/ui/Field";
import { formatAuftragLookupLabel } from "@/features/globalUpload/formatAuftragLookupLabel";
import type { BookedHoursRow } from "@/lib/bookedHours";
import { formatBookedHoursWhen, formatHours } from "@/lib/bookedHours";
import { getRecentAuftraege, searchAuftraege, type AuftragLookup } from "@/lib/material";
import { rebookProjectHours, bookedHoursRebookEntry } from "@/lib/stundenUmbuchung";
import { toast } from "@/lib/toast";
import { PROJECT_OVERVIEW_NESTED_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

export function BookedHoursRebookDialog({
  open,
  projectId,
  rows,
  onClose,
  onRebooked,
}: {
  open: boolean;
  projectId: number;
  rows: BookedHoursRow[];
  onClose: () => void;
  onRebooked: () => void;
}) {
  const [target, setTarget] = useState<AuftragLookup | null>(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTarget(null);
    setReason("");
    setSaving(false);
  }, [open, projectId]);

  const trimmed = reason.trim();
  const canSubmit = trimmed.length > 0 && target != null && rows.length > 0 && !saving;
  const total = rows.reduce((sum, row) => sum + row.hours, 0);

  async function submit() {
    if (!target || !trimmed) return;
    setSaving(true);
    try {
      const result = await rebookProjectHours(projectId, {
        ziel_auftrag_id: target.id,
        begruendung: trimmed,
        entries: rows.map(bookedHoursRebookEntry),
      });
      const count = result.moved_count;
      toast.success(
        count === 1
          ? "1 Buchung wurde umgebucht. Die Notiz steht in beiden Projekten."
          : `${count} Buchungen wurden umgebucht. Die Notiz steht in beiden Projekten.`,
      );
      onRebooked();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Umbuchung fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? () => {} : onClose}
      dirty={trimmed.length > 0 || target != null}
      size="md"
      zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
      plainBackdrop
      title="Stunden umbuchen"
      description="Die markierten Buchungen werden auf das Zielprojekt gelegt. In beiden Projekten entsteht eine bürointerne Notiz."
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={saving} />
          <Button type="button" disabled={!canSubmit} loading={saving} onClick={() => void submit()}>
            {formatHours(total)} umbuchen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <ul className="max-h-40 space-y-1 overflow-y-auto rounded-ios border border-separator bg-fill-quaternary/20 px-3 py-2 text-sm">
          {rows.map((row) => (
            <li key={row.id} className="text-label-secondary">
              <span className="font-medium text-label">{row.person}</span>
              {" · "}
              {formatBookedHoursWhen(row.when)}
              {" · "}
              <span className="tabular-nums">{formatHours(row.hours)}</span>
              {row.positionLabel ? (
                <>
                  {" · "}
                  {row.positionLabel}
                </>
              ) : null}
              {" · "}
              {row.sourceLabel}
            </li>
          ))}
        </ul>
        <Autocomplete<AuftragLookup>
          key={`${projectId}-${open ? "open" : "closed"}`}
          label="Zielprojekt"
          placeholder="Nummer, Kunde oder Ort …"
          minChars={0}
          required
          loadOptions={async (query) => {
            const list = query.trim()
              ? await searchAuftraege(query)
              : await getRecentAuftraege({ limit: 15 });
            return list
              .filter(
                (row) =>
                  row.id !== projectId &&
                  String(row.workflow_status || "").trim().toLowerCase() !== "abgelehnt",
              )
              .map((row) => ({
                key: String(row.id),
                label: formatAuftragLookupLabel(row),
                description: row.beschreibung || undefined,
                value: row,
              }));
          }}
          onSelect={(option) => setTarget(option?.value ?? null)}
        />
        <TextareaInput
          id="stunden-umbuchung-grund"
          label="Begründung"
          required
          rows={3}
          value={reason}
          disabled={saving}
          placeholder="Zum Beispiel: Zeit wurde auf dem falschen Projekt erfasst."
          onChange={(event) => setReason(event.target.value)}
        />
        <p className="text-xs text-label-tertiary">
          Buchungen, die schon auf einem Stundennachweis stehen, bleiben dort und können hier nicht
          umgebucht werden.
        </p>
      </div>
    </Dialog>
  );
}
